/**
 * Build BSE StAR MF v2 /order_new payload (purchase lumpsum).
 * @see BSE Postman "order_new_purchase Physical"
 */
export function buildOrderNewPurchasePayload({
  clientCode,
  memberId,
  scheme,
  amount,
  memOrdRefId,
  physOrDemat = "p",
  email = "",
  mobnum = "",
  folio = "",
  isFresh = true,
  paymentRefId = "",
  kycPassed = true,
  dpc = true,
} = {}) {
  const ucc = String(clientCode || "").trim();
  const member = String(memberId || process.env.REACT_APP_BSE_MEMBER_ID || "63164").trim();
  const ref =
    String(memOrdRefId || "").trim() ||
    `${Date.now()}${Math.floor(Math.random() * 9000 + 1000)}`.slice(0, 16);

  return {
    data: {
      orders: [
        {
          type: "p",
          mem_ord_ref_id: ref.slice(0, 16),
          investor: { ucc },
          member,
          scheme: String(scheme || "").trim(),
          amount: Number(amount),
          cur: "INR",
          is_units: false,
          all_units: false,
          min_redeem_flag: false,
          folio: String(folio || ""),
          is_fresh: !!isFresh,
          phys_or_demat: String(physOrDemat || "p").toLowerCase(),
          payment_ref_id: String(paymentRefId || ""),
          email: String(email || ""),
          mobnum: String(mobnum || ""),
          kyc_passed: !!kycPassed,
          dpc: !!dpc,
          is_nomination_opted: false,
          nomination_auth_mode: "",
        },
      ],
    },
  };
}

export function buildOrderListPayload({
  memberId,
  clientCode,
  openClose = "a",
  status,
  types,
  placedAtAfter,
  placedAtBefore,
  start = 0,
  length = 100,
} = {}) {
  const filter = { open_close: openClose };
  const ucc = String(clientCode || "").trim();
  if (ucc) {
    filter.investor = { ucc };
    filter.member_code = String(memberId || process.env.REACT_APP_BSE_MEMBER_ID || "63164").trim();
  }
  if (Array.isArray(status) && status.length) filter.status = status;
  if (Array.isArray(types) && types.length) filter.type = types;
  // BSE demo rejects ISO placed_at_* in many cases — filter by date in UI when needed.
  if (placedAtAfter) filter.placed_at_after = placedAtAfter;
  if (placedAtBefore) filter.placed_at_before = placedAtBefore;

  return {
    data: {
      fields: ["ALL"],
      start,
      length,
      count_only: false,
      filter_param: filter,
    },
  };
}

export function buildOrderGetPayload(orderId, openClose) {
  const payload = { data: { id: orderId } };
  if (openClose) payload.data.open_close = openClose;
  return payload;
}

/** Modify an existing open order (BSE /order_update). */
export function buildOrderUpdatePayload({
  orderId,
  clientCode,
  memberId,
  scheme,
  amount,
  type = "p",
  memOrdRefId,
  physOrDemat,
  folio = "",
  ...rest
} = {}) {
  const order = {
    id: orderId,
    type: String(type || "p").toLowerCase(),
    scheme: String(scheme || "").trim(),
    member: String(memberId || process.env.REACT_APP_BSE_MEMBER_ID || "63164").trim(),
    investor: { ucc: String(clientCode || "").trim() },
    amount: Number(amount),
    cur: "INR",
    folio: String(folio || ""),
    ...rest,
  };
  if (memOrdRefId) order.mem_ord_ref_id = String(memOrdRefId).trim();
  if (physOrDemat) order.phys_or_demat = String(physOrDemat).toLowerCase();
  return { data: { orders: [order] } };
}

export function buildOrderCancelPayload(orderId) {
  return { data: { id: orderId } };
}

export function isOrderCancellable(status) {
  const s = String(status || "").toLowerCase();
  return ["received", "pending", "open", "created", "queued"].includes(s);
}

function uccTrim(value) {
  return value === undefined || value === null ? "" : String(value).trim();
}

function getUccDepositoryList(uccDetails) {
  if (!uccDetails || typeof uccDetails !== "object") return [];
  if (Array.isArray(uccDetails.depository)) return uccDetails.depository.filter(Boolean);
  if (uccDetails.depository && typeof uccDetails.depository === "object") {
    return [uccDetails.depository];
  }
  return [];
}

function getUccStatusDepositoryRows(uccDetails) {
  const statusObj = uccDetails?.ucc_status_object;
  if (!statusObj) return [];
  if (Array.isArray(statusObj.depository)) return statusObj.depository;
  if (Array.isArray(statusObj) && statusObj.length && statusObj[0]?.depository_code) {
    return statusObj;
  }
  return [];
}

function isUccDepositoryVerified(uccDetails, dep) {
  if (dep?.is_verified === true) return true;
  const rows = getUccStatusDepositoryRows(uccDetails);
  const dp = uccTrim(dep?.dp_id);
  const cl = uccTrim(dep?.client_id);
  if (!rows.length) return !!(dp && cl);
  const match = rows.find((r) => uccTrim(r?.dp_id) === dp && uccTrim(r?.client_id) === cl);
  return match ? String(match.verified_status || "").toUpperCase() === "TRUE" : false;
}

function isUccDematOrderReady(uccDetails) {
  if (!uccDetails?.is_client_demat) return false;
  const dep = getUccDepositoryList(uccDetails).find((d) => d?.is_default) || getUccDepositoryList(uccDetails)[0];
  if (!dep || !uccTrim(dep.dp_id) || !uccTrim(dep.client_id)) return false;
  if (dep.is_verified === true) return true;
  if (isUccDepositoryVerified(uccDetails, dep)) return true;
  if (uccDetails.is_client_physical === false) return true;
  return false;
}

/** Mirror backend mode rules for UI defaults. */
export function resolveUccOrderModes(uccDetails) {
  const allowed = [];
  if (uccDetails?.is_client_physical === true) allowed.push("p");
  if (isUccDematOrderReady(uccDetails)) allowed.push("d");

  let hint = "";
  if (!allowed.length) {
    hint = "This UCC is not configured for orders yet. Complete registration on BSE.";
  } else if (allowed.length === 1 && allowed[0] === "d") {
    hint = "This UCC is demat-only. Orders will use Demat mode.";
  } else if (allowed.length === 1 && allowed[0] === "p") {
    hint = "This UCC is physical-only. Orders will use Physical mode.";
  }

  return {
    allowed,
    defaultMode: allowed[0] || "d",
    hint,
  };
}

const ORDER_TYPE_LABELS = { p: "Purchase", r: "Redeem", s: "Switch" };

export function mapBseOrderListItem(item, index = 0) {
  const row = item && typeof item === "object" ? item : {};
  const type = String(row.type || "").toLowerCase();
  return {
    id: row.id || row.mem_ord_ref_id || index + 1,
    orderNo: row.id || row.mem_ord_ref_id || "-",
    memOrdRefId: row.mem_ord_ref_id || "",
    date: row.placed_at || "",
    scheme: row.src_scheme_name || row.scheme || "-",
    schemeCode: row.scheme || "",
    transactionType: ORDER_TYPE_LABELS[type] || type || "-",
    type,
    amount: row.amount ?? "",
    status: row.status || "-",
    ucc: row.investor?.ucc || "",
    raw: row,
  };
}

export function parseOrderListResponse(body) {
  const lists = body?.data?.lists;
  if (!Array.isArray(lists)) return [];
  return lists.map((item, i) => mapBseOrderListItem(item, i));
}

const PHYS_DEMAT_LABELS = { p: "Physical", d: "Demat" };

export function mapBseOrderDetail(item) {
  const row = item && typeof item === "object" ? item : {};
  const type = String(row.type || "").toLowerCase();
  const phys = String(row.phys_or_demat || "").toLowerCase();
  const rejection = row.rejection_reason || {};
  const srcInfo = row.order_src_info || {};
  const memDetails = row.info?.mem_details || {};
  const depository = row.depository_acct || {};

  return {
    orderNo: row.id || "-",
    memOrdRefId: row.mem_ord_ref_id || "",
    exchOrderId: row.exch_order_id || "",
    ucc: row.investor?.ucc || "",
    status: row.status || "-",
    type,
    transactionType: ORDER_TYPE_LABELS[type] || type || "-",
    scheme: row.scheme || "-",
    isin: srcInfo.src_isin || "",
    amount: row.amount ?? "",
    currency: row.cur || "INR",
    folio: row.folio_num || "",
    physOrDemat: PHYS_DEMAT_LABELS[phys] || phys || "-",
    email: row.email || "",
    mobnum: row.mobnum || "",
    placedAt: row.placed_at || "",
    euin: memDetails.euin || "",
    remarks: row.remarks || "",
    rejectionBy: rejection.by || "",
    rejectionReason: rejection.reason || "",
    paymentRefId: row.payment_ref_id || "",
    orderAction: row.order_action || "",
    schemeTat: row.scheme_tat || "",
    history: Array.isArray(row.history) ? row.history : [],
    depository: depository.depository || "",
    dpId: depository.dp_id || "",
    clientId: depository.client_id || "",
    raw: row,
  };
}

export function parseOrderGetResponse(body) {
  const data = body?.data;
  if (!data || typeof data !== "object") return null;
  return mapBseOrderDetail(data);
}

export function extractBseApiError(err) {
  const body = err?.response?.data || {};
  const nested = body.data || {};
  const messages = body.messages || nested.messages || nested.data?.messages;
  if (Array.isArray(messages) && messages.length) {
    return messages
      .map((m) => {
        const field = m?.field ? ` (${m.field})` : "";
        const vals = Array.isArray(m?.vals) ? m.vals.filter(Boolean) : [];
        const detail = vals.find((v) => v.length > 20) || vals[1] || vals[0] || "";
        const code = m?.errcode || "error";
        return detail ? `${code}${field}: ${detail}` : `${code}${field}`;
      })
      .join("; ");
  }
  return body.message || err?.message || "BSE request failed";
}

export function buildPaymentDetailPayload({ orderId, paymentRefId, bankTxnRef } = {}) {
  const data = {};
  if (paymentRefId) data.payment_ref_id = String(paymentRefId);
  else if (orderId) data.order_id = String(orderId);
  else if (bankTxnRef) data.bank_txn_ref = String(bankTxnRef);
  return { data };
}
